import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGetRefSize from '../../../hooks/useGetRefSize';
import useGetInsights from '../api/queries/useGetInsights';
import { useUsers } from '../context/UserManagementContext';
import { User } from '../types/User';
import { UserRow } from './UsersTable/types';
import UserManagementInsights from './UserManagementInsights';
import UsersTableToolbar from './UsersTableToolbar/UsersTableToolbar';
import UsersTable from './UsersTable';
import UserManagementBulkActions from './UserManagementBulkActions/UserManagementBulkActions';
import { PopupProvider } from '../context/PopupProvider';
import SendEnrollmentRequest from './SendEnrollmentRequest/SendEnrollmentRequest';
import ManageProtectedGroups from './ManageProtectedGroups/ManageProtectedGroups';

const AdUsers: FC = () => {
    const tableContainerTef = useRef<HTMLDivElement | null>(null);
    const { height: tableHeight } = useGetRefSize(tableContainerTef);
    const { insights, loading: isInsightsLoading } = useGetInsights();
    const [selectedRowsIds, setSelectedRowsIds] = useState<UserRow['id'][]>([]);
    const {
        users = [],
        mutate: fetchUsers,
        systemData,
        userSession,
        fetchProtectedEntities,
        savedProtectedEntities,
        setToolbarExtraContent
    } = useUsers();

    const selectedUsers = useMemo(
        () => users.filter((user) => selectedRowsIds.includes(user.guid)),
        [users, selectedRowsIds]
    );

    const handleUpdateProtectedEntities = useCallback(() => {
        fetchProtectedEntities();
        fetchUsers();
    }, [fetchProtectedEntities, fetchUsers]);

    useEffect(() => {
        const layoutData = (
            <PopupProvider>
                <div className="flex flex-row gap-2">
                    <SendEnrollmentRequest userSession={userSession} systemData={systemData} />
                    <ManageProtectedGroups
                        userSession={userSession}
                        savedProtectedEntities={savedProtectedEntities}
                        onUpdateProtectedEntities={handleUpdateProtectedEntities}
                    />
                </div>
            </PopupProvider>
        );
        setToolbarExtraContent(layoutData);
        return () => setToolbarExtraContent('');
    }, [
        setToolbarExtraContent,
        systemData?.Settings?.OfflineMode,
        userSession,
        savedProtectedEntities,
        handleUpdateProtectedEntities
    ]);

    const onSelectRows = useCallback((ids: UserRow['id'][]) => {
        setSelectedRowsIds(ids);
    }, []);

    return (
        <div className="flex-1 px-6 pt-4 h-full flex flex-col gap-4 overflow-x-hidden">
            <UserManagementInsights insights={insights} isLoading={isInsightsLoading} />
            <div className="w-full flex flex-col gap-6">
                <UsersTableToolbar />
            </div>
            <div ref={tableContainerTef} className="flex flex-1 overflow-hidden">
                <UsersTable height={tableHeight} selectedRowsIds={selectedRowsIds} onSelectRows={onSelectRows} />
            </div>
            <div className="mt-[-16px]">
                <UserManagementBulkActions selectedUsers={selectedUsers} onClose={() => setSelectedRowsIds([])} />
            </div>
        </div>
    );
};

export default AdUsers;
